import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthFetch } from '../hooks/useAuthFetch';

const ETAPES = ['Qualification', 'Proposition', 'Négociation', 'Gagnée', 'Perdue'];
const ETAPE_COLORS = {
  Qualification: '#41B073',
  Proposition: '#f59e0b',
  Négociation: '#ec4899',
  Gagnée: '#10b981',
  Perdue: '#ef4444',
};

const fmt = v => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const is = { width: '100%', padding: '8px 10px', borderRadius: 6, background: '#0f1117', border: '1px solid #2a2d3e', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };

function Badge({ text, color }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: (color || '#41B073') + '22', color: color || '#41B073', border: `1px solid ${(color || '#41B073')}44` }}>
      {text}
    </span>
  );
}

function OppPanel({ oppId, onClose, onSave }) {
  const fetchWithAuth = useAuthFetch();
  const [opp, setOpp] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchWithAuth(`/api/opportunites/${oppId}`).then(r => r.json()).then(setOpp);
    fetchWithAuth('/api/clients?limit=200').then(r => r.json()).then(d => setClients(d.data || []));
  }, [oppId]);

  useEffect(() => { if (opp) setForm({ ...opp }); }, [opp]);

  const save = async () => {
    await fetchWithAuth(`/api/opportunites/${oppId}`, { method: 'PUT', body: JSON.stringify(form) });
    onSave();
    onClose();
  };

  const del = async () => {
    if (!window.confirm('Supprimer cette opportunité ?')) return;
    await fetchWithAuth(`/api/opportunites/${oppId}`, { method: 'DELETE' });
    onSave();
    onClose();
  };

  if (!opp) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300 }} />
      <div className="side-panel modal-scroll" style={{ width: 420, background: '#1a1d2e', borderLeft: '1px solid #2a2d3e', zIndex: 301, overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: 16 }}>{editing ? 'Modifier' : 'Opportunité'}</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {!editing && <button onClick={() => setEditing(true)} style={{ background: '#41B073', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12 }}>Modifier</button>}
            {!editing && <button onClick={del} style={{ background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12 }}>Supprimer</button>}
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20 }}>✕</button>
          </div>
        </div>

        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div><label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Client</label>
              <select value={form.client_id || ''} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} style={{ ...is, cursor: 'pointer' }}>
                {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select></div>
            <div><label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Titre</label>
              <input value={form.titre || ''} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} style={is} /></div>
            <div><label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Valeur €</label>
              <input type="number" value={form.valeur || ''} onChange={e => setForm(f => ({ ...f, valeur: e.target.value }))} style={is} /></div>
            <div><label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Probabilité %</label>
              <input type="number" min="0" max="100" value={form.probabilite || ''} onChange={e => setForm(f => ({ ...f, probabilite: e.target.value }))} style={is} /></div>
            <div><label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Étape</label>
              <select value={form.etape || ''} onChange={e => setForm(f => ({ ...f, etape: e.target.value }))} style={{ ...is, cursor: 'pointer' }}>
                {ETAPES.map(e => <option key={e}>{e}</option>)}
              </select></div>
            <div><label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Clôture prévue</label>
              <input type="date" value={form.date_cloture_prevue || ''} onChange={e => setForm(f => ({ ...f, date_cloture_prevue: e.target.value }))} style={{ ...is, colorScheme: 'dark' }} /></div>
            <div><label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Notes</label>
              <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...is, height: 80, resize: 'vertical' }} /></div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={save} style={{ flex: 1, padding: 10, borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' }}>Enregistrer</button>
              <button onClick={() => setEditing(false)} style={{ padding: '10px 16px', borderRadius: 8, background: 'transparent', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontFamily: 'inherit' }}>Annuler</button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}><Badge text={opp.etape} color={ETAPE_COLORS[opp.etape]} /></div>
            {[['Client', opp.client_nom], ['Valeur', fmt(opp.valeur)], ['Probabilité', `${opp.probabilite}%`], ['Clôture prévue', fmtDate(opp.date_cloture_prevue)], ['Commercial', opp.commercial_nom], ['Source', opp.source]].map(([k, v]) => v && (
              <div key={k} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{k}</div>
                <div style={{ fontSize: 14, color: '#e2e8f0' }}>{v}</div>
              </div>
            ))}
            {opp.notes && <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 13, color: '#94a3b8', background: '#12141f', padding: 12, borderRadius: 6, whiteSpace: 'pre-wrap' }}>{opp.notes}</div>
            </div>}
          </div>
        )}
      </div>
    </>
  );
}

export default function OpportunitesPage() {
  const fetchWithAuth = useAuthFetch();
  const location = useLocation();
  const [opps, setOpps] = useState([]);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(location.state?.etape ? { etape: location.state.etape } : {});
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(location.state?.oppId || null);
  const [showModal, setShowModal] = useState(false);
  const [newOpp, setNewOpp] = useState({ etape: 'Qualification', probabilite: 50, source: 'Réseau personnel' });
  const [formErrors, setFormErrors] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 200, sort: sortField, order: sortDir });
    if (filters.etape) params.set('etape', filters.etape);
    fetchWithAuth(`/api/opportunites?${params}`)
      .then(r => r.json())
      .then(data => { setOpps(data.data || []); setTotal(data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
    fetchWithAuth('/api/clients?limit=200').then(r => r.json()).then(d => setClients(d.data || []));
  }, [sortField, sortDir, filters.etape]);

  useEffect(() => { load(); }, [load]);

  const filteredOpps = opps.filter(o =>
    !search || o.titre?.toLowerCase().includes(search.toLowerCase()) || o.client_nom?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSort = field => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const createOpp = async () => {
    const errors = {};
    if (!newOpp.client_id) errors.client_id = true;
    if (!newOpp.titre) errors.titre = true;
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    setFormErrors({});
    await fetchWithAuth('/api/opportunites', { method: 'POST', body: JSON.stringify(newOpp) });
    setShowModal(false);
    setNewOpp({ etape: 'Qualification', probabilite: 50, source: 'Réseau personnel' });
    load();
  };

  const SortTh = ({ field, children }) => (
    <th onClick={() => toggleSort(field)} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
      {children} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Opportunités <span style={{ fontSize: 14, color: '#64748b', fontWeight: 400 }}>({total})</span></h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ display: 'flex', background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 8, overflow: 'hidden' }}>
            {['kanban', 'table'].map(v => (
              <button key={v} onClick={() => setView(v)} style={{ padding: '7px 14px', border: 'none', background: view === v ? '#41B073' : 'transparent', color: view === v ? '#fff' : '#64748b', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                {v === 'kanban' ? '▦ Kanban' : '≡ Tableau'}
              </button>
            ))}
          </div>
          <button onClick={() => setShowModal(true)} style={{ padding: '9px 18px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>+ Nouvelle opportunité</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher..." style={{ ...is, minWidth: 200 }} />
        <select value={filters.etape || ''} onChange={e => setFilters(f => ({ ...f, etape: e.target.value }))} style={{ ...is, cursor: 'pointer' }}>
          <option value="">Toutes étapes</option>
          {ETAPES.map(e => <option key={e}>{e}</option>)}
        </select>
      </div>

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Chargement...</div> : view === 'kanban' ? (
        <div className="kanban-scroll" style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16 }}>
          {ETAPES.map(etape => {
            const col = filteredOpps.filter(o => o.etape === etape);
            const colTotal = col.reduce((s, o) => s + (o.valeur || 0), 0);
            return (
              <div key={etape} style={{ minWidth: 240, flex: '0 0 240px' }}>
                <div style={{ background: ETAPE_COLORS[etape] + '22', border: `1px solid ${ETAPE_COLORS[etape]}44`, borderRadius: '8px 8px 0 0', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: ETAPE_COLORS[etape] }}>{etape}</span>
                  <span style={{ fontSize: 11, color: ETAPE_COLORS[etape], opacity: 0.8 }}>{col.length} · {fmt(colTotal)}</span>
                </div>
                <div style={{ background: '#12141f', border: `1px solid ${ETAPE_COLORS[etape]}22`, borderTop: 'none', borderRadius: '0 0 8px 8px', minHeight: 200, padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {col.map(o => (
                    <div key={o.id} onClick={() => setSelectedId(o.id)} style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderLeft: `3px solid ${ETAPE_COLORS[etape]}`, borderRadius: 8, padding: '10px 12px', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = `0 2px 12px ${ETAPE_COLORS[etape]}33`}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4, fontWeight: 500 }}>{o.client_nom}</div>
                      <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, marginBottom: 6 }}>{o.titre}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, color: ETAPE_COLORS[etape], fontWeight: 700 }}>{fmt(o.valeur)}</span>
                        <span style={{ fontSize: 11, color: '#64748b' }}>{o.probabilite}%</span>
                      </div>
                      {o.date_cloture_prevue && <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>📅 {fmtDate(o.date_cloture_prevue)}</div>}
                    </div>
                  ))}
                  {col.length === 0 && <div style={{ fontSize: 12, color: '#2a2d3e', textAlign: 'center', padding: 20 }}>Aucune</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2d3e' }}>
                <SortTh field="titre">Titre</SortTh>
                <th style={{ padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left' }}>Client</th>
                <SortTh field="etape">Étape</SortTh>
                <SortTh field="valeur">Valeur</SortTh>
                <th style={{ padding: '10px 14px', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', textAlign: 'left' }}>Proba.</th>
                <SortTh field="date_cloture_prevue">Clôture</SortTh>
              </tr>
            </thead>
            <tbody>
              {filteredOpps.length === 0
                ? <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Aucune opportunité</td></tr>
                : filteredOpps.map((o, i) => (
                  <tr key={o.id} onClick={() => setSelectedId(o.id)} style={{ borderBottom: '1px solid #2a2d3e', cursor: 'pointer', background: i % 2 === 0 ? 'transparent' : '#ffffff05' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#41B07315'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : '#ffffff05'}>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{o.titre}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{o.client_nom}</td>
                    <td style={{ padding: '11px 14px' }}><Badge text={o.etape} color={ETAPE_COLORS[o.etape]} /></td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#41B073', fontWeight: 600 }}>{fmt(o.valeur)}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{o.probabilite}%</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{fmtDate(o.date_cloture_prevue)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && <OppPanel oppId={selectedId} onClose={() => setSelectedId(null)} onSave={load} />}

      {showModal && (
        <>
          <div onClick={() => setShowModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }} />
          <div className="center-modal modal-scroll" style={{ width: 460, background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 14, padding: 28, zIndex: 201, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: 16 }}>Nouvelle opportunité</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Client *</label>
              <select value={newOpp.client_id || ''} onChange={e => setNewOpp(f => ({ ...f, client_id: e.target.value }))} style={{ ...is, borderColor: formErrors.client_id ? '#ef4444' : '#2a2d3e', cursor: 'pointer' }}>
                <option value="">— Choisir —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            {[['titre', 'Titre *'], ['valeur', 'Valeur estimée €'], ['probabilite', 'Probabilité %']].map(([k, l]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>{l}</label>
                <input value={newOpp[k] || ''} onChange={e => setNewOpp(f => ({ ...f, [k]: e.target.value }))} style={{ ...is, borderColor: formErrors[k] ? '#ef4444' : '#2a2d3e' }} />
              </div>
            ))}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Clôture prévue</label>
              <input type="date" value={newOpp.date_cloture_prevue || ''} onChange={e => setNewOpp(f => ({ ...f, date_cloture_prevue: e.target.value }))} style={{ ...is, colorScheme: 'dark' }} />
            </div>
            {[['etape', 'Étape', ETAPES], ['source', 'Source', ['Réseau personnel', 'LinkedIn', 'Client existant', 'Partenaire / Apporteur', 'CCI / BGE', 'Salon / Événement', 'Site web / SEO', 'Inbound', 'Autre']]].map(([k, l, opts]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>{l}</label>
                <select value={newOpp[k] || ''} onChange={e => setNewOpp(f => ({ ...f, [k]: e.target.value }))} style={{ ...is, cursor: 'pointer' }}>{opts.map(o => <option key={o}>{o}</option>)}</select>
              </div>
            ))}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Notes</label>
              <textarea value={newOpp.notes || ''} onChange={e => setNewOpp(f => ({ ...f, notes: e.target.value }))} style={{ ...is, height: 60, resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={createOpp} style={{ flex: 1, padding: '10px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: 'inherit' }}>Créer</button>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: 8, background: 'transparent', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>Annuler</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
